import type { AspectHit, ChartResult } from '@astrally/shared';

/**
 * Circular tropical wheel. Ascendant sits at 9 o'clock; the zodiac runs
 * counterclockwise. Renders signs, houses (when available), planets and
 * aspect lines. Exact-time charts only.
 */

const SIGN_GLYPHS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];
const PLANET_GLYPHS: Record<string, string> = {
  sun: '☉',
  moon: '☽',
  mercury: '☿',
  venus: '♀',
  mars: '♂',
  jupiter: '♃',
  saturn: '♄',
  uranus: '♅',
  neptune: '♆',
  pluto: '♇',
};

/**
 * Aspect line colours are tokens, not literals: the light palette needs MORE
 * alpha than the dark one for the same apparent weight, so the two themes
 * carry different values behind the same names.
 */
const ASPECT_COLOR: Record<AspectHit['type'], string> = {
  conjunction: 'var(--aspect-conjunction)',
  opposition: 'var(--aspect-opposition)',
  square: 'var(--aspect-square)',
  trine: 'var(--aspect-trine)',
  sextile: 'var(--aspect-sextile)',
  // Minors are drawn faintly so the wheel stays readable.
  quincunx: 'var(--aspect-minor-gold)',
  'semi-sextile': 'var(--aspect-minor-violet)',
  'semi-square': 'var(--aspect-minor-red)',
  sesquiquadrate: 'var(--aspect-minor-red)',
};

export function WesternWheel({ chart }: { chart: ChartResult }) {
  const positions = chart.positions ?? [];
  const angles = chart.western?.angles;
  const houses = chart.western?.houses;
  const aspects = chart.western?.aspects ?? [];
  if (positions.length === 0) return null;

  const cx = 160;
  const cy = 160;
  const asc = angles?.ascendant ?? 0;
  const toXY = (lon: number, r: number): [number, number] => {
    const deg = 180 + (asc - lon);
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };

  // Collision-adjusted planet radii.
  const sorted = [...positions]
    .filter((p) => p.planet !== 'rahu' && p.planet !== 'ketu')
    .sort((a, b) => a.lonTropical - b.lonTropical);
  const radiusFor = new Map<string, number>();
  let prevLon = -999;
  let level = 0;
  for (const p of sorted) {
    level = p.lonTropical - prevLon < 10 ? (level + 1) % 3 : 0;
    radiusFor.set(p.planet, 108 - level * 17);
    prevLon = p.lonTropical;
  }

  return (
    <div className="card" style={{ padding: 12 }}>
      <svg viewBox="0 0 320 320" style={{ width: '100%', height: 'auto' }}>
        <circle cx={cx} cy={cy} r={150} fill="none" stroke="var(--border-strong)" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r={126} fill="none" stroke="var(--border)" strokeWidth="1" />
        <circle cx={cx} cy={cy} r={58} fill="none" stroke="var(--border)" strokeWidth="1" />

        {/* Sign boundaries + glyphs */}
        {Array.from({ length: 12 }, (_, i) => {
          const [x1, y1] = toXY(i * 30, 126);
          const [x2, y2] = toXY(i * 30, 150);
          const [gx, gy] = toXY(i * 30 + 15, 138);
          return (
            <g key={i}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--border-strong)" strokeWidth="1" />
              <text x={gx} y={gy + 4} textAnchor="middle" fontSize="12" fill="var(--text-dim)">
                {SIGN_GLYPHS[i]}
              </text>
            </g>
          );
        })}

        {/* House cusps */}
        {houses?.cusps.map((cusp, i) => {
          const angular = i === 0 || i === 3 || i === 6 || i === 9;
          const [x1, y1] = toXY(cusp, 58);
          const [x2, y2] = toXY(cusp, 126);
          const [nx, ny] = toXY(cusp + 12, 68);
          return (
            <g key={i}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={angular ? 'var(--text-faint)' : 'var(--border)'}
                strokeWidth={angular ? 1.4 : 0.7}
              />
              <text x={nx} y={ny + 3} textAnchor="middle" fontSize="8" fill="var(--text-faint)">
                {i + 1}
              </text>
            </g>
          );
        })}

        {/* Aspect lines */}
        {aspects
          .filter((a) => a.a !== 'ascendant' && a.a !== 'mc' && a.b !== 'ascendant' && a.b !== 'mc')
          .map((a, i) => {
            const pa = positions.find((p) => p.planet === a.a);
            const pb = positions.find((p) => p.planet === a.b);
            if (!pa || !pb) return null;
            const [x1, y1] = toXY(pa.lonTropical, 56);
            const [x2, y2] = toXY(pb.lonTropical, 56);
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={ASPECT_COLOR[a.type]}
                strokeWidth={a.type === 'conjunction' ? 0 : 1}
              />
            );
          })}

        {/* Planets */}
        {sorted.map((p) => {
          const r = radiusFor.get(p.planet) ?? 108;
          const [x, y] = toXY(p.lonTropical, r);
          const [tx, ty] = toXY(p.lonTropical, r - 15);
          return (
            <g key={p.planet}>
              <text x={x} y={y + 5} textAnchor="middle" fontSize="14" fill="var(--gold-ink)">
                {PLANET_GLYPHS[p.planet] ?? p.planet.slice(0, 2)}
              </text>
              <text x={tx} y={ty + 3} textAnchor="middle" fontSize="7" fill="var(--text-faint)">
                {Math.floor(p.tropical.degInSign)}°{p.retrograde ? ' ℞' : ''}
              </text>
            </g>
          );
        })}

        {/* Asc/MC markers */}
        {angles && (
          <>
            <AscMcMark label="AC" lon={angles.ascendant} toXY={toXY} />
            <AscMcMark label="MC" lon={angles.mc} toXY={toXY} />
          </>
        )}
      </svg>
    </div>
  );
}

function AscMcMark({
  label,
  lon,
  toXY,
}: {
  label: string;
  lon: number;
  toXY: (lon: number, r: number) => [number, number];
}) {
  const [x, y] = toXY(lon, 158);
  return (
    <text x={x} y={y + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--accent-ink)">
      {label}
    </text>
  );
}
